import {
  type ScheduledConsultationDTO,
  type ScheduledConsultationUserDetailsDTO,
  ConsultationRequestUserRelationDTO,
  ScheduledConsultationParticipantStatusDTO,
  ScheduledConsultationStatusDTO,
  type ErrorResponseDto,
  ErrorResponseDtoCodeEnum
} from '@/generated'
import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChipProps } from '@/components/consultationrequest/ConsultationChip.vue'
import { parseISO } from 'date-fns'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { ConsultationService } from '@/services/telehealthApi/ConsultationService'
import { NoticeType } from '@/stores/models/Notice'
import { useConsultationRequestStore } from '@/stores/consultationRequest'
import { useNoticeStore } from '@/stores/notice'
import { HttpStatusCode, isAxiosError } from 'axios'

const NO_LONGER_ACTIONABLE_MESSAGE =
  'This scheduled consultation can no longer be accepted or declined. It was withdrawn by the creator or has already been processed.'

export function useScheduledConsultationRequest(
  scheduledConsultation: MaybeRefOrGetter<ScheduledConsultationDTO>
) {
  const noticeStore = useNoticeStore()
  const { t } = useI18n()
  const { user } = storeToRefs(useUserStore())
  const { refreshIncomingConsultationRequestList, refreshScheduledConsultationById } =
    useConsultationRequestStore()
  const consultationRequest = computed(() => toValue(scheduledConsultation))

  const consultationId = computed<string>(() => consultationRequest.value.id)
  const consultationDate = computed<Date>(() =>
    parseISO(consultationRequest.value.scheduledStartTime)
  )
  const currentDate = ref<Date>(new Date())

  function setCurrentTime(): void {
    currentDate.value = new Date()
  }

  const reportRequiredRecipients = computed(() => {
    return new Set(
      consultationRequest.value.participants
        .filter((participant) => participant.shouldWriteReport)
        .map((participant) => participant.user.userName)
    )
  })

  const reportAllowedRecipients = computed<Set<string>>(() => {
    const recipients = consultationRequest.value.participants ?? []
    const rejectReportStatuses: string[] = [
      ScheduledConsultationParticipantStatusDTO.DECLINED,
      ScheduledConsultationParticipantStatusDTO.DELETED,
      ScheduledConsultationParticipantStatusDTO.NOT_PARTICIPATED
    ]
    return new Set(
      recipients
        .filter((recipient) => !rejectReportStatuses.includes(recipient.participationStatus))
        .map((recipient) => recipient.user.userName)
    )
  })

  const isOutgoingRequest = computed<boolean>(
    () => consultationRequest.value.relationToUser === ConsultationRequestUserRelationDTO.OUTGOING
  )

  const isIncomingRequest = computed<boolean>(
    () => consultationRequest.value.relationToUser === ConsultationRequestUserRelationDTO.INCOMING
  )

  const isStatusCreated = computed<boolean>(
    () => consultationRequest.value.status === ScheduledConsultationStatusDTO.CREATED
  )

  const isStatusInProgress = computed<boolean>(
    () => consultationRequest.value.status === ScheduledConsultationStatusDTO.IN_PROGRESS
  )

  const isStatusReportsPending = computed<boolean>(
    () => consultationRequest.value.status === ScheduledConsultationStatusDTO.REPORT_PENDING
  )

  const consultationFinishedStatuses = new Set<ScheduledConsultationStatusDTO>([
    ScheduledConsultationStatusDTO.COMPLETED,
    ScheduledConsultationStatusDTO.DELETED
  ])
  const isFinishedConsultation = computed<boolean>(() =>
    consultationFinishedStatuses.has(consultationRequest.value.status)
  )

  const isStatusReportsCompleted = computed<boolean>(() => {
    return consultationRequest.value.status === ScheduledConsultationStatusDTO.REPORTS_COMPLETED
  })

  const isStatusDeleted = computed<boolean>(() => {
    return consultationRequest.value.status === ScheduledConsultationStatusDTO.DELETED
  })

  const isStatusCompleted = computed<boolean>(() => {
    return consultationRequest.value.status === ScheduledConsultationStatusDTO.COMPLETED
  })

  const isParticipationPending = computed<boolean>(() => {
    const participant = consultationRequest.value.participants.find(
      (p) => p.user.userName === user.value?.userName
    )
    return participant?.participationStatus === ScheduledConsultationParticipantStatusDTO.PENDING
  })

  const isParticipationAccepted = computed<boolean>(() => {
    const participant = consultationRequest.value.participants.find(
      (p) => p.user.userName === user.value?.userName
    )
    return participant?.participationStatus === ScheduledConsultationParticipantStatusDTO.ACCEPTED
  })

  const hasUserJoinedConsultation = computed<boolean>(() => {
    const requesterHasJoined =
      consultationRequest.value.requestedBy?.user.userName === user.value?.userName &&
      (!!consultationRequest.value.requestedBy?.joinedCallAt ||
        consultationRequest.value.isUserInCall)

    const participant = consultationRequest.value.participants.find(
      (p) => p.user.userName === user.value?.userName
    )
    const participantHasJoined =
      !!participant?.joinedCallAt ||
      !!participant?.isUserInCall ||
      (!isOutgoingRequest.value && consultationRequest.value.isUserInCall)

    return requesterHasJoined || participantHasJoined
  })

  const currentUserParticipantDetails = computed<ScheduledConsultationUserDetailsDTO | undefined>(
    () => {
      const userName = user.value?.userName
      if (!userName) return undefined
      if (consultationRequest.value.requestedBy?.user.userName === userName) {
        return consultationRequest.value.requestedBy
      }
      return consultationRequest.value.participants.find((p) => p.user.userName === userName)
    }
  )

  const hasCurrentUserFinalizedReport = computed<boolean>(
    () => currentUserParticipantDetails.value?.reportFinalized === true
  )

  const statusChipData = computed<{
    label: string
    icon?: string
    variant: ChipProps['variant']
  } | null>(() => {
    if (consultationRequest.value.status === ScheduledConsultationStatusDTO.IN_PROGRESS) {
      return {
        label: t('Consultation in progress'),
        icon: 'custom:phoneCall',
        variant: 'success'
      }
    }
    if (consultationRequest.value.status === ScheduledConsultationStatusDTO.REPORT_PENDING) {
      return {
        label: t('Consultation report pending'),
        variant: 'warning'
      }
    }
    if (consultationRequest.value.status === ScheduledConsultationStatusDTO.REPORTS_COMPLETED) {
      return {
        label: t('Consultation reports complete'),
        variant: 'success'
      }
    }
    if (consultationRequest.value.status === ScheduledConsultationStatusDTO.COMPLETED) {
      return {
        label: t('Completed'),
        variant: 'success'
      }
    }
    if (
      consultationRequest.value.status === ScheduledConsultationStatusDTO.DELETED &&
      consultationRequest.value.relationToUser === ConsultationRequestUserRelationDTO.INCOMING
    ) {
      return {
        label: t('Deleted by creator'),
        variant: 'error'
      }
    }

    return null
  })

  async function refreshAfterParticipationChange(id: string): Promise<void> {
    await Promise.all([
      refreshIncomingConsultationRequestList(),
      refreshScheduledConsultationById(id, { withClear: false })
    ])
  }

  function isNoLongerActionable(error: unknown): boolean {
    return isAxiosError(error) && error.response?.status === HttpStatusCode.Conflict
  }

  async function acceptScheduledConsultationById(id: string): Promise<void> {
    try {
      await ConsultationService.acceptScheduledConsultationRequest(id)
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: isNoLongerActionable(error)
          ? NO_LONGER_ACTIONABLE_MESSAGE
          : 'Accepting the scheduled consultation request {id} failed',
        messageParameters: { id }
      })
    }
    await refreshAfterParticipationChange(id)
  }

  function declineErrorMessage(error: unknown): string {
    if (isAxiosError<ErrorResponseDto>(error)) {
      if (error.response?.data?.code === ErrorResponseDtoCodeEnum.NOT_FOUND) {
        return 'Scheduled consultation no longer exist'
      }
      if (isNoLongerActionable(error)) {
        return NO_LONGER_ACTIONABLE_MESSAGE
      }
      if (error.response?.data?.code === ErrorResponseDtoCodeEnum.VALIDATION_FAILED) {
        return 'Scheduled consultation validation failed during decline'
      }
    }
    return 'Declining the scheduled consultation request {id} failed'
  }

  async function declineScheduledConsultationById(id: string): Promise<void> {
    try {
      await ConsultationService.declineScheduledConsultationRequest(id)
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: declineErrorMessage(error),
        messageParameters: { id }
      })
    }
    await refreshAfterParticipationChange(id)
  }

  return {
    currentDate,
    consultationId,
    consultationDate,
    reportRequiredRecipients,
    reportAllowedRecipients,
    isFinishedConsultation,
    isOutgoingRequest,
    isIncomingRequest,
    statusChipData,
    isStatusCreated,
    isStatusInProgress,
    isStatusReportsPending,
    isStatusReportsCompleted,
    isStatusDeleted,
    isStatusCompleted,
    isParticipationPending,
    isParticipationAccepted,
    hasUserJoinedConsultation,
    hasCurrentUserFinalizedReport,
    setCurrentTime,
    acceptScheduledConsultationById,
    declineScheduledConsultationById
  }
}
